import { useMemo } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import {
    COLOR_AXIS_LINE,
    COLOR_DIVIDER_HIGHLIGHT,
    COLOR_DIVIDER_NEUTRAL,
    COLOR_HIGHLIGHT_FILL,
    DOMAIN_PAD_FRACTION,
    DOT_COLORS_BY_QUADRANT,
    DOT_RADIUS,
    PLOT_PADDING,
    QuadrantKey,
    ViewConfig,
} from "./PopularityTrendGapChart.constants";
import {
    getPlottableKeywords,
    getQuadrantKey,
    toPixel,
    PlottableKeyword,
} from "./PopularityTrendGapChart.helpers";
import { PopularityTrendGapKeyword } from "@apiHooks/useCategoryProfilePopularityTrendGap";

interface Props {
    keywords:   PopularityTrendGapKeyword[];
    config:     ViewConfig;
    width:      number;
    height:     number;
    /** Called when the user hovers a dot. Null clears the active dot; cx/cy are the
     *  hovered dot's SVG pixel coordinates, used by the parent to anchor the tooltip. */
    onDotHover: (kw: PlottableKeyword | null, cx?: number, cy?: number) => void;
}

const PopularityTrendGapScatter: FCX<Props> = ({
    keywords,
    config,
    width,
    height,
    onDotHover,
}) => {
    const points = useMemo(() => getPlottableKeywords(keywords, config), [keywords, config]);

    const { minX, maxX, minY, maxY } = useMemo(() => {
        if (points.length === 0) return { minX: 0, maxX: 1, minY: 0, maxY: 1 };
        const xs = points.map(p => p.x);
        const ys = points.map(p => p.y);
        const rawMinX = Math.min(...xs);
        const rawMaxX = Math.max(...xs);
        const rawMinY = Math.min(...ys);
        const rawMaxY = Math.max(...ys);
        // Symmetric per-axis padding so extreme dots don't pin to the plot border.
        const padX = (rawMaxX - rawMinX) * DOMAIN_PAD_FRACTION;
        const padY = (rawMaxY - rawMinY) * DOMAIN_PAD_FRACTION;
        return {
            minX: rawMinX - padX,
            maxX: rawMaxX + padX,
            minY: rawMinY - padY,
            maxY: rawMaxY + padY,
        };
    }, [points]);

    // Convert data coordinates to SVG pixels.
    // Y is inverted: higher value = lower cy (top of SVG).
    const toCx = (x: number) => toPixel(x, minX, maxX, width,  PLOT_PADDING);
    const toCy = (y: number) => height - toPixel(y, minY, maxY, height, PLOT_PADDING);

    const midX = width  / 2;
    const midY = height / 2;

    // Build quadrant highlight rects first (rendered under the dividers and dots).
    const quadrantRects = useMemo((): Array<{ key: QuadrantKey; x: number; y: number; w: number; h: number }> => [
        { key: 'topLeft',     x: 0,    y: 0,    w: midX,         h: midY },
        { key: 'topRight',    x: midX, y: 0,    w: width - midX, h: midY },
        { key: 'bottomLeft',  x: 0,    y: midY, w: midX,         h: height - midY },
        { key: 'bottomRight', x: midX, y: midY, w: width - midX, h: height - midY },
    ], [midX, midY, width, height]);

    const isHighlighted = (key: QuadrantKey): boolean =>
        config.highlightedQuadrants.includes(key);

    // Center divider split into four half-segments. Each segment borders two
    // quadrants and is tinted only when one of them is highlighted; otherwise neutral.
    const dividerSegments = [
        { id: 'divider_v_top',    x1: midX, y1: 0,    x2: midX,  y2: midY,   tinted: isHighlighted('topLeft')    || isHighlighted('topRight') },
        { id: 'divider_v_bottom', x1: midX, y1: midY, x2: midX,  y2: height, tinted: isHighlighted('bottomLeft') || isHighlighted('bottomRight') },
        { id: 'divider_h_left',   x1: 0,    y1: midY, x2: midX,  y2: midY,   tinted: isHighlighted('topLeft')    || isHighlighted('bottomLeft') },
        { id: 'divider_h_right',  x1: midX, y1: midY, x2: width, y2: midY,   tinted: isHighlighted('topRight')   || isHighlighted('bottomRight') },
    ];

    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={`Scatter plot of ${points.length} keywords: ${config.xLabel} (horizontal) versus ${config.yLabel} (vertical)`}
        >
            {/* Solid left + bottom axis lines */}
            <line x1={0} y1={0} x2={0} y2={height} stroke={COLOR_AXIS_LINE} strokeWidth={1} />
            <line x1={0} y1={height} x2={width} y2={height} stroke={COLOR_AXIS_LINE} strokeWidth={1} />

            {/* Highlighted-quadrant background fills */}
            {quadrantRects.map(({ key, x, y, w, h }) =>
                isHighlighted(key) ? (
                    <rect
                        key={`fill-${key}`}
                        x={x} y={y} width={w} height={h}
                        fill={COLOR_HIGHLIGHT_FILL}
                    />
                ) : null,
            )}

            {/* Center dividers — each half-segment tinted only if an adjacent quadrant is highlighted */}
            {dividerSegments.map(({ id, x1, y1, x2, y2, tinted }) => (
                <line
                    key={id}
                    x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke={tinted ? COLOR_DIVIDER_HIGHLIGHT : COLOR_DIVIDER_NEUTRAL}
                    strokeWidth={1}
                    strokeDasharray="4 4"
                    data-testid={id}
                />
            ))}

            {/* Dots */}
            {points.map((kw, i) => {
                const cx = toCx(kw.x);
                const cy = toCy(kw.y);
                const quadrant = getQuadrantKey(cx, cy, width, height);
                const fill = DOT_COLORS_BY_QUADRANT[quadrant];
                return (
                    <circle
                        key={kw.wordId ?? i}
                        cx={cx}
                        cy={cy}
                        r={DOT_RADIUS}
                        fill={fill}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => onDotHover(kw, cx, cy)}
                        onMouseLeave={() => onDotHover(null)}
                        data-testid={`scatterDot_${i}`}
                    />
                );
            })}
        </svg>
    );
};

export default PopularityTrendGapScatter;
